// Text-to-Speech component for AI responses
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Volume2, VolumeX, ChevronDown } from "lucide-react";

// Available languages aligned with STT languages
const availableLanguages = [
  { code: "auto", name: "Auto", flag: "🌐" },
  { code: "en-US", name: "English", flag: "🇺🇸" },
  { code: "ar-SA", name: "العربية", flag: "🇸🇦" },
  { code: "es-ES", name: "Español", flag: "🇪🇸" },
  { code: "pt-BR", name: "Português", flag: "🇧🇷" },
];

// Voice mapping for each language (browser-specific voices)
const getVoicesForLanguage = (langCode) => {
  const voices = window.speechSynthesis?.getVoices() || [];
  
  // Filter voices by language code
  const langPrefix = langCode.split("-")[0];
  const matchingVoices = voices.filter((voice) => {
    const voiceLang = voice.lang.toLowerCase();
    return voiceLang.startsWith(langPrefix.toLowerCase());
  });

  // If no voices found, return default voices
  if (matchingVoices.length === 0) {
    return voices.filter((voice) => voice.lang.startsWith("en"));
  }

  return matchingVoices;
};

// Detect browser language (similar to STT)
const detectBrowserLanguage = () => {
  if (typeof navigator === "undefined") return "en-US";
  
  const browserLang = navigator.language || navigator.userLanguage;
  const browserLangs = navigator.languages || [browserLang];
  
  const languageMap = {
    "en": "en-US",
    "en-us": "en-US",
    "en-gb": "en-US",
    "ar": "ar-SA",
    "ar-sa": "ar-SA",
    "ar-eg": "ar-SA",
    "es": "es-ES",
    "es-es": "es-ES",
    "es-mx": "es-ES",
    "pt": "pt-BR",
    "pt-br": "pt-BR",
    "pt-pt": "pt-BR",
  };
  
  for (const lang of browserLangs) {
    const langCode = lang.toLowerCase();
    if (languageMap[langCode]) {
      return languageMap[langCode];
    }
    const langPrefix = langCode.split("-")[0];
    if (languageMap[langPrefix]) {
      return languageMap[langPrefix];
    }
  }
  
  return "en-US";
};

const TextToSpeech = ({ text, className = "" }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("auto");
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [availableVoices, setAvailableVoices] = useState([]);
  const languageDropdownRef = useRef(null);
  const languageButtonRef = useRef(null);
  const utteranceRef = useRef(null);
  const synthRef = useRef(null);

  // Get effective language (resolve "auto" to detected language)
  const effectiveLanguage = useMemo(() => {
    if (selectedLanguage === "auto") {
      return detectBrowserLanguage();
    }
    return selectedLanguage;
  }, [selectedLanguage]);

  // Load available voices
  useEffect(() => {
    const loadVoices = () => {
      const voices = window.speechSynthesis?.getVoices() || [];
      setAvailableVoices(voices);
      
      // Auto-select first voice for current language if none selected
      if (!selectedVoice && voices.length > 0) {
        const langVoices = getVoicesForLanguage(effectiveLanguage);
        if (langVoices.length > 0) {
          setSelectedVoice(langVoices[0]);
        } else if (voices.length > 0) {
          setSelectedVoice(voices[0]);
        }
      }
    };

    loadVoices();
    
    // Some browsers load voices asynchronously
    if (window.speechSynthesis) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }

    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.onvoiceschanged = null;
      }
    };
  }, [effectiveLanguage, selectedVoice]);

  // Update selected voice when language changes
  useEffect(() => {
    if (availableVoices.length > 0) {
      const langVoices = getVoicesForLanguage(effectiveLanguage);
      if (langVoices.length > 0) {
        setSelectedVoice(langVoices[0]);
      } else if (availableVoices.length > 0) {
        setSelectedVoice(availableVoices[0]);
      }
    }
  }, [effectiveLanguage, availableVoices]);

  // Get current language info
  const getCurrentLanguageInfo = () => {
    if (selectedLanguage === "auto") {
      const detectedLang = detectBrowserLanguage();
      const detectedLangInfo = availableLanguages.find(
        (lang) => lang.code === detectedLang
      );
      return {
        code: "auto",
        name: `Auto (${detectedLangInfo?.name || "English"})`,
        flag: "🌐",
        detectedCode: detectedLang,
      };
    }
    return (
      availableLanguages.find((lang) => lang.code === selectedLanguage) ||
      availableLanguages[1]
    );
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (synthRef.current) {
        synthRef.current.cancel();
      }
      if (utteranceRef.current) {
        utteranceRef.current = null;
      }
    };
  }, []);

  // Stop speech when component unmounts or text changes
  useEffect(() => {
    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      setIsPlaying(false);
    };
  }, [text]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        languageDropdownRef.current &&
        !languageDropdownRef.current.contains(event.target) &&
        languageButtonRef.current &&
        !languageButtonRef.current.contains(event.target)
      ) {
        setShowLanguageMenu(false);
      }
    }
    if (showLanguageMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showLanguageMenu]);

  // Extract plain text from markdown (remove markdown syntax)
  const extractPlainText = useCallback((markdownText) => {
    if (!markdownText) return "";
    
    // Remove code blocks
    let plain = markdownText.replace(/```[\s\S]*?```/g, "");
    // Remove inline code
    plain = plain.replace(/`[^`]+`/g, "");
    // Remove links but keep text
    plain = plain.replace(/\[([^\]]+)\]\([^\)]+\)/g, "$1");
    // Remove images
    plain = plain.replace(/!\[([^\]]*)\]\([^\)]+\)/g, "");
    // Remove headers
    plain = plain.replace(/^#{1,6}\s+/gm, "");
    // Remove bold/italic
    plain = plain.replace(/\*\*([^\*]+)\*\*/g, "$1");
    plain = plain.replace(/\*([^\*]+)\*/g, "$1");
    plain = plain.replace(/__([^_]+)__/g, "$1");
    plain = plain.replace(/_([^_]+)_/g, "$1");
    // Remove list markers
    plain = plain.replace(/^[\*\-\+]\s+/gm, "");
    plain = plain.replace(/^\d+\.\s+/gm, "");
    // Remove horizontal rules
    plain = plain.replace(/^---$/gm, "");
    // Clean up extra whitespace
    plain = plain.replace(/\n{3,}/g, "\n\n");
    plain = plain.trim();
    
    return plain;
  }, []);

  // Toggle play/stop
  const toggle = useCallback(() => {
    if (!window.speechSynthesis) {
      alert("Text-to-speech is not supported in this browser.");
      return;
    }

    const synth = window.speechSynthesis;

    if (isPlaying) {
      // Stop speech
      synth.cancel();
      setIsPlaying(false);
      return;
    }

    // Start speech
    const plainText = extractPlainText(text);
    if (!plainText.trim()) {
      return;
    }

    const utterance = new SpeechSynthesisUtterance(plainText);
    
    // Set language
    utterance.lang = effectiveLanguage;
    
    // Set voice if available
    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }
    
    // Set speech properties
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;

    // Handle events
    utterance.onstart = () => {
      setIsPlaying(true);
    };

    utterance.onend = () => {
      setIsPlaying(false);
      utteranceRef.current = null;
    };

    utterance.onerror = (event) => {
      console.error("Speech synthesis error:", event);
      setIsPlaying(false);
      utteranceRef.current = null;
    };

    utteranceRef.current = utterance;
    synthRef.current = synth;
    synth.speak(utterance);
  }, [text, isPlaying, effectiveLanguage, selectedVoice, extractPlainText]);

  // Get voices for current language
  const currentLanguageVoices = useMemo(() => {
    return getVoicesForLanguage(effectiveLanguage);
  }, [effectiveLanguage]);

  const currentLangInfo = getCurrentLanguageInfo();

  // Check if TTS is supported
  if (!window.speechSynthesis) {
    return null;
  }

  return (
    <div className={`flex items-center gap-1 relative ${className}`}>
      {/* Language selector button */}
      <button
        ref={languageButtonRef}
        type="button"
        className="flex items-center gap-1 p-1.5 rounded-lg text-fg-50 hover:bg-bg-50/20 transition-colors text-xs"
        title={`TTS language: ${currentLangInfo.name}`}
        onClick={() => setShowLanguageMenu(!showLanguageMenu)}
      >
        <span className="text-sm">{currentLangInfo.flag}</span>
        <ChevronDown size={12} />
      </button>

      {/* Language dropdown */}
      {showLanguageMenu && (
        <div
          ref={languageDropdownRef}
          className="absolute bottom-full right-0 mb-2 bg-bg-60 rounded-lg shadow-xl border border-bd-50 min-w-[140px] z-50 max-h-[300px] overflow-y-auto"
        >
          <div className="px-3 py-2 bg-bg-50 text-xs font-medium text-fg-50 border-b border-bd-50">
            Language
          </div>
          {availableLanguages.map((lang) => (
            <button
              key={lang.code}
              type="button"
              className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors ${
                selectedLanguage === lang.code
                  ? "bg-bg-50 text-ac-02"
                  : "text-fg-50 hover:bg-bg-50"
              }`}
              onClick={() => {
                setSelectedLanguage(lang.code);
                setShowLanguageMenu(false);
              }}
            >
              <span className="text-base">{lang.flag}</span>
              <span className="text-xs">{lang.name}</span>
            </button>
          ))}
          
          {/* Voice selector for current language */}
          {currentLanguageVoices.length > 0 && (
            <>
              <div className="px-3 py-2 bg-bg-50 text-xs font-medium text-fg-50 border-t border-b border-bd-50">
                Voices ({currentLanguageVoices.length})
              </div>
              {currentLanguageVoices.slice(0, 5).map((voice, index) => (
                <button
                  key={voice.name || index}
                  type="button"
                  className={`w-full flex items-center gap-2 px-3 py-2 text-left text-xs transition-colors ${
                    selectedVoice?.name === voice.name
                      ? "bg-bg-50 text-ac-02"
                      : "text-fg-50 hover:bg-bg-50"
                  }`}
                  onClick={() => {
                    setSelectedVoice(voice);
                    setShowLanguageMenu(false);
                  }}
                  title={voice.name}
                >
                  <span className="truncate">{voice.name}</span>
                </button>
              ))}
            </>
          )}
        </div>
      )}

      {/* Play/Stop button */}
      <button
        type="button"
        className={`p-1.5 rounded-lg transition-colors ${
          isPlaying
            ? "bg-ac-02 text-white dark:text-black hover:bg-ac-01"
            : "text-fg-50 hover:bg-bg-50/20"
        }`}
        title={isPlaying ? "Stop reading" : `Read aloud (${currentLangInfo.name})`}
        onClick={toggle}
        aria-pressed={isPlaying}
      >
        {isPlaying ? (
          <VolumeX size={16} strokeWidth={1.5} />
        ) : (
          <Volume2 size={16} strokeWidth={1.5} />
        )}
      </button>
    </div>
  );
};

export default TextToSpeech;


